import { motion } from 'framer-motion';
import { MapPin, PlusCircle, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { typography } from '../../styles/typography';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input';
import { stockOutApi } from './stockOutApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const createEmptyRow = () => ({
  id: Date.now() + Math.random(),
  stockItemId: '',
  stockItemName: '',
  availableQuantity: 0,
  quantity: '',
  action: 'Return', // 'Sell' or 'Return'
  sellingPrice: '',
  reason: '',
});

const Stock_out = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize from location state if available
  const initialState = location.state || {};
  const hasItemsFromState = initialState.items && Array.isArray(initialState.items) && initialState.items.length > 0;
  const isEditMode = initialState.editMode || false;
  
  // Helper function to normalize date to YYYY-MM-DD
  const normalizeDate = (dateValue) => {
    if (!dateValue) return getTodayDate();
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return getTodayDate();
  };

  // Store original date for edit mode (should not change)
  const [originalDate, setOriginalDate] = useState(() => {
    // In edit mode, preserve the original date from the entry
    if (isEditMode) {
      // Try entry object first
      if (initialState.entry?.stock_date || initialState.entry?.event_date || initialState.entry?.date) {
        const dateValue = initialState.entry.stock_date || initialState.entry.event_date || initialState.entry.date;
        console.log('Stock_out: Original date from entry:', dateValue);
        return normalizeDate(dateValue);
      }
      // Try items array (from StockOutView navigation)
      if (initialState.items && initialState.items.length > 0) {
        const firstItem = initialState.items[0];
        const dateValue = firstItem.stock_date || firstItem.event_date || firstItem.date || initialState.date;
        if (dateValue) {
          console.log('Stock_out: Original date from items array:', dateValue);
          return normalizeDate(dateValue);
        }
      }
      // Try initialState.date
      if (initialState.date) {
        console.log('Stock_out: Original date from initialState.date:', initialState.date);
        return normalizeDate(initialState.date);
      }
      console.warn('Stock_out: ⚠️ Edit mode but no original date found!');
    }
    return null;
  });

  const [selectedDate, setSelectedDate] = useState(() => {
    // Priority: initialState.date > entry date > today
    if (initialState.date) {
      return normalizeDate(initialState.date);
    }
    if (initialState.entry?.stock_date || initialState.entry?.event_date || initialState.entry?.date) {
      const dateValue = initialState.entry.stock_date || initialState.entry.event_date || initialState.entry.date;
      return normalizeDate(dateValue);
    }
    return getTodayDate();
  });
  
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [formRows, setFormRows] = useState(() => {
    // Initialize form rows from state if in edit mode
    if (isEditMode) {
      // First try to use entry object
      if (initialState.entry) {
        const entry = initialState.entry;
        console.log('Stock_out: ✅ Initializing form rows from entry object');
        console.log('Stock_out: Entry object:', JSON.stringify(entry, null, 2));
        console.log('Stock_out: Entry keys:', Object.keys(entry));
        
        // Try multiple possible field names for stock item ID
        const stockItemId = entry.stock_item_id || entry.item_id || entry.stockItemId || entry.stockItem?.stock_item_id || entry.stockItem?.item_id || '';
        const quantity = entry.quantity || entry.qty || 0;
        const reason = entry.reason || '';
        const sellingPrice = entry.selling_price || entry.sellingPrice || null;
        
        console.log('Stock_out: Extracted values from entry:', {
          stockItemId,
          quantity,
          reason,
          sellingPrice,
        });
        
        const initialRow = {
          id: Date.now(),
          stockItemId: String(stockItemId), // Ensure it's a string
          stockItemName: entry.item_name || entry.itemName || entry.stock_item_name || entry.stockItem?.item_name || '',
          availableQuantity: 0, // Will be updated when stock items are loaded
          quantity: String(quantity),
          action: (reason === 'Sold' || reason === 'Sell') ? 'Sell' : 'Return',
          sellingPrice: sellingPrice ? String(sellingPrice) : '',
          reason: entry.notes || entry.reason || '',
        };
        
        console.log('Stock_out: Initial row created from entry:', JSON.stringify(initialRow, null, 2));
        
        return [initialRow];
      }
      
      // Fallback: try to use items array (from StockOutView navigation)
      if (initialState.items && initialState.items.length > 0) {
        console.log('Stock_out: ✅ Initializing form rows from items array in edit mode');
        console.log('Stock_out: Items array:', JSON.stringify(initialState.items, null, 2));
        
        const firstItem = initialState.items[0];
        console.log('Stock_out: First item keys:', Object.keys(firstItem));
        console.log('Stock_out: First item full:', JSON.stringify(firstItem, null, 2));
        
        return initialState.items.map((item, idx) => {
          // Try to extract stock item ID from various possible field names
          // The items array from StockOutView might have different structure
          // IMPORTANT: Check all possible field names including nested structures
          const stockItemId = item.stock_item_id || 
                             item.item_id || 
                             item.stockItemId || 
                             item.id || 
                             item.stockItem?.stock_item_id ||
                             item.stockItem?.item_id ||
                             '';
          const quantity = item.quantity || item.quantityOrWeight || item.qty || 0;
          const action = item.action || (item.reason === 'Sold' || item.reason === 'Sell' ? 'Sell' : 'Return') || 'Return';
          const sellingPrice = item.selling_price || item.sellingPrice || item.pricePerKg || '';
          
          console.log('Stock_out: Item', idx, 'extracted:', {
            stockItemId,
            quantity,
            action,
            sellingPrice,
            'item keys': Object.keys(item),
            'item.stock_item_id': item.stock_item_id,
            'item.item_id': item.item_id,
            'item.id': item.id,
          });
          
          const formRow = {
            id: Date.now() + idx,
            stockItemId: String(stockItemId),
            stockItemName: item.item_name || item.itemName || item.name || '',
            availableQuantity: 0,
            quantity: String(quantity),
            action: action,
            sellingPrice: String(sellingPrice),
            reason: item.notes || item.reason || '',
          };
          
          console.log('Stock_out: Created formRow:', JSON.stringify(formRow, null, 2));
          
          return formRow;
        });
      }
      
      console.warn('Stock_out: ⚠️ Edit mode but no entry or items found');
    }
    
    // Non-edit mode: use items from state if available
    if (hasItemsFromState && initialState.items && initialState.items.length > 0) {
      return initialState.items.map((item, idx) => ({
        id: Date.now() + idx,
        stockItemId: item.id || item.stockItemId || '',
        stockItemName: item.itemName || '',
        availableQuantity: 0,
        quantity: String(item.quantityOrWeight || ''),
        action: 'Return',
        sellingPrice: String(item.pricePerKg || ''),
        reason: '',
      }));
    }
    
    return [createEmptyRow()];
  });
  const [referenceId, setReferenceId] = useState(initialState.entry?.reference_id || initialState.referenceId || '');
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isLoadingStockItems, setIsLoadingStockItems] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  // Debug: Log initial state on mount
  useEffect(() => {
    console.log('Stock_out: Component mounted');
    console.log('Stock_out: Initial state:', initialState);
    console.log('Stock_out: Is edit mode:', isEditMode);
    console.log('Stock_out: Entry data:', initialState.entry);
    if (initialState.entry) {
      console.log('Stock_out: Entry keys:', Object.keys(initialState.entry));
      console.log('Stock_out: Entry stock_item_id:', initialState.entry.stock_item_id);
      console.log('Stock_out: Entry item_id:', initialState.entry.item_id);
      console.log('Stock_out: Entry quantity:', initialState.entry.quantity);
      console.log('Stock_out: Entry reason:', initialState.entry.reason);
      console.log('Stock_out: Entry selling_price:', initialState.entry.selling_price);
    }
    console.log('Stock_out: Site ID from state:', initialState.siteId);
    console.log('Stock_out: Date from state:', initialState.date);
    console.log('Stock_out: Reference ID from state:', initialState.referenceId);
    console.log('Stock_out: Initial formRows:', formRows);
    if (formRows.length > 0) {
      console.log('Stock_out: First formRow:', formRows[0]);
      console.log('Stock_out: First formRow stockItemId:', formRows[0].stockItemId);
    }
  }, []);

  // Debug: Log formRows changes
  useEffect(() => {
    console.log('Stock_out: FormRows updated:', JSON.stringify(formRows, null, 2));
    if (formRows.length > 0) {
      console.log('Stock_out: First formRow stockItemId:', formRows[0].stockItemId);
      console.log('Stock_out: First formRow quantity:', formRows[0].quantity);
      console.log('Stock_out: First formRow action:', formRows[0].action);
    }
  }, [formRows]);

  // Debug: Log selectedSite changes
  useEffect(() => {
    console.log('Stock_out: SelectedSite updated:', selectedSite);
  }, [selectedSite]);

  // Debug: Log stockItems changes
  useEffect(() => {
    console.log('Stock_out: StockItems updated:', stockItems.length, 'items');
    if (stockItems.length > 0 && formRows.length > 0 && formRows[0].stockItemId) {
      const matchingItem = stockItems.find(item => 
        (item.stock_item_id || item.item_id) === formRows[0].stockItemId
      );
      console.log('Stock_out: Matching stock item for formRow:', matchingItem);
    }
  }, [stockItems]);

  // Mobile view detection
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const updateView = (event) => {
      setIsMobileView(event.matches);
    };

    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesData = await stockOutApi.getSites();
        const sitesList = Array.isArray(sitesData) 
          ? sitesData 
          : sitesData?.sites || sitesData?.data || [];
        // Sort sites alphabetically by name
        const sortedSites = [...sitesList].sort((a, b) => {
          const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
          const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        
        console.log('Stock_out: Sites loaded:', sortedSites.length);
        console.log('Stock_out: Initial state siteId:', initialState.siteId);
        console.log('Stock_out: Is edit mode:', isEditMode);
        
        // Set selected site from state only if in edit mode, otherwise don't auto-select
        if (isEditMode && initialState.siteId && sortedSites.length > 0) {
          const site = sortedSites.find(s => (s.site_id || s.id) === initialState.siteId);
          console.log('Stock_out: Found site for siteId:', site);
          if (site) {
            setSelectedSite(site);
          }
        }
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites:', error);
        setError('Failed to load sites. Please refresh the page.');
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Fetch stock items when site is selected
  useEffect(() => {
    const fetchStockItems = async () => {
      if (!selectedSite?.site_id && !selectedSite?.id) {
        setStockItems([]);
        return;
      }

      setIsLoadingStockItems(true);
      try {
        const siteId = selectedSite.site_id || selectedSite.id;
        console.log('Stock_out: Fetching stock items for site:', siteId);
        
        const result = await stockOutApi.getStockItems({
          siteId: siteId,
          limit: 200,
        });
        
        // In edit mode, don't filter by available quantity (we need to show the item even if it's now 0)
        // Otherwise, filter items with available quantity > 0
        const items = isEditMode 
          ? (result.items || [])
          : (result.items || []).filter(item => (item.current_quantity || 0) > 0);
        
        console.log('Stock_out: Stock items loaded:', items.length);
        
        setStockItems(items);
        
        // Update available quantity in form rows once stock items are loaded
        // Use functional update to get current formRows
        setFormRows(prev => {
          console.log('Stock_out: Current formRows in update:', JSON.stringify(prev, null, 2));
          console.log('Stock_out: Checking if formRows need update. Length:', prev.length, 'First row stockItemId:', prev[0]?.stockItemId);
          
          if (prev.length > 0) {
            const firstRow = prev[0];
            console.log('Stock_out: First row details:', {
              stockItemId: firstRow.stockItemId,
              quantity: firstRow.quantity,
              action: firstRow.action,
            });
            
            if (firstRow.stockItemId) {
              console.log('Stock_out: Updating form rows with stock item data');
              console.log('Stock_out: Looking for stockItemId:', firstRow.stockItemId);
              console.log('Stock_out: Available stock items:', items.map(i => ({
                id: i.stock_item_id || i.item_id,
                name: i.item_name || i.name
              })));
              
              return prev.map(row => {
                if (row.stockItemId) {
                  const stockItem = items.find(item => {
                    const itemId = item.stock_item_id || item.item_id;
                    const rowId = row.stockItemId;
                    const match = String(itemId) === String(rowId);
                    console.log('Stock_out: Comparing itemId:', itemId, '(type:', typeof itemId, ') with rowId:', rowId, '(type:', typeof rowId, ') Match:', match);
                    return match;
                  });
                  if (stockItem) {
                    console.log('Stock_out: ✅ Found matching stock item:', stockItem);
                    return {
                      ...row,
                      availableQuantity: stockItem.current_quantity || 0,
                      stockItemName: stockItem.item_name || stockItem.name || row.stockItemName,
                    };
                  } else {
                    console.warn('Stock_out: ❌ No matching stock item found for:', row.stockItemId);
                    console.log('Stock_out: Available item IDs:', items.map(i => ({
                      stock_item_id: i.stock_item_id,
                      item_id: i.item_id,
                      name: i.item_name || i.name
                    })));
                  }
                } else {
                  console.warn('Stock_out: Row has no stockItemId:', row);
                }
                return row;
              });
            } else {
              console.warn('Stock_out: First row has no stockItemId, cannot update');
            }
          }
          return prev;
        });
      } catch (error) {
        console.error('Error fetching stock items:', error);
        setError('Failed to load stock items.');
      } finally {
        setIsLoadingStockItems(false);
      }
    };
    fetchStockItems();
  }, [selectedSite]);

  const handleSiteChange = (siteId) => {
    const site = sites.find(s => (s.site_id || s.id) === siteId);
    setSelectedSite(site || null);
    // Reset form rows when site changes (unless in edit mode)
    if (!isEditMode) {
      setFormRows([createEmptyRow()]);
    }
  };

  const handleRowChange = (rowId, field, value) => {
    setFormRows((prev) => {
      const next = prev.map((row) => {
        if (row.id === rowId) {
          const updated = { ...row, [field]: value };
          
          // When stock item is selected, update available quantity
          if (field === 'stockItemId' && value) {
            const stockItem = stockItems.find(item => 
              (item.stock_item_id || item.item_id) === value
            );
            if (stockItem) {
              updated.stockItemName = stockItem.item_name || stockItem.name || '';
              updated.availableQuantity = stockItem.current_quantity || 0;
            }
          }
          
          // Clear selling price when action changes to Return
          if (field === 'action' && value === 'Return') {
            updated.sellingPrice = '';
          }
          
          return updated;
        }
        return row;
      });
      return next;
    });
  };

  const handleAddRow = () => {
    setFormRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleRemoveRow = (rowId) => {
    setFormRows((prev) => {
      const filtered = prev.filter((row) => row.id !== rowId);
      return filtered.length > 0 ? filtered : [createEmptyRow()];
    });
  };

  const validateForm = () => {
    const errors = [];
    
    if (!selectedSite) {
      errors.push('Please select a site');
    }
    
    if (formRows.length === 0) {
      errors.push('Please add at least one stock item');
    }
    
    formRows.forEach((row, index) => {
      if (!row.stockItemId) {
        errors.push(`Row ${index + 1}: Please select a stock item`);
      }
      if (!row.quantity || parseFloat(row.quantity) <= 0) {
        errors.push(`Row ${index + 1}: Please enter a valid quantity`);
      }
      // Ensure quantity is a whole number (no decimals)
      if (row.quantity && !Number.isInteger(parseFloat(row.quantity))) {
        errors.push(`Row ${index + 1}: Quantity must be a whole number (no decimals)`);
      }
      if (parseFloat(row.quantity) > row.availableQuantity) {
        errors.push(`Row ${index + 1}: Quantity exceeds available stock (${row.availableQuantity})`);
      }
      if (row.action === 'Sell' && (!row.sellingPrice || parseFloat(row.sellingPrice) < 0)) {
        errors.push(`Row ${index + 1}: Please enter a valid selling price`);
      }
    });
    
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      console.warn('Stock_out: Submit already in progress, ignoring duplicate submission');
      return;
    }
    
    setError('');
    setSuccess('');
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if we're in edit mode - try multiple ways to get the ledger ID
      // Backend expects ledger_id, but it might be stored as different field names
      console.log('Stock_out: Submit - Extracting ledger ID...');
      console.log('Stock_out: Submit - initialState.entryId:', initialState.entryId);
      console.log('Stock_out: Submit - initialState.entry:', initialState.entry);
      console.log('Stock_out: Submit - initialState.entry keys:', initialState.entry ? Object.keys(initialState.entry) : 'no entry');
      console.log('Stock_out: Submit - initialState.items:', initialState.items);
      
      // Try to extract ledger ID from multiple sources:
      // 1. Direct entryId from state
      // 2. From entry object
      // 3. From first item in items array (if coming from StockOutView)
      let ledgerId = initialState.entryId || 
                     initialState.entry?.ledger_id ||
                     initialState.entry?.stock_out_record_id || 
                     initialState.entry?.stock_out_id || 
                     initialState.entry?.id ||
                     initialState.entry?.ledger_entry_id ||
                     null;
      
      // If still no ledgerId and we have items array, try to extract from first item
      if (!ledgerId && initialState.items && Array.isArray(initialState.items) && initialState.items.length > 0) {
        const firstItem = initialState.items[0];
        ledgerId = firstItem.ledger_id ||
                   firstItem.stock_out_record_id ||
                   firstItem.stock_out_id ||
                   firstItem.id ||
                   firstItem.ledger_entry_id ||
                   null;
        console.log('Stock_out: Submit - Extracted ledgerId from items array:', ledgerId);
      }
      
      console.log('Stock_out: Submit - isEditMode:', isEditMode);
      console.log('Stock_out: Submit - ledgerId resolved:', ledgerId);
      console.log('Stock_out: Submit - Full initialState:', JSON.stringify(initialState, null, 2));
      
      // If in edit mode, update the existing entry
      if (isEditMode && ledgerId) {
        console.log('Stock_out: ✅ Updating existing entry with ledger_id:', ledgerId);
        const firstRow = formRows[0];
        
        // CRITICAL: Use the ORIGINAL date from the entry, not today's date
        // This prevents updating entries for the wrong date (e.g., updating Wednesday entry with Friday's date)
        let dateToUse = originalDate;
        
        if (!dateToUse) {
          // Fallback: try to get from entry or items
          if (initialState.entry?.stock_date || initialState.entry?.event_date || initialState.entry?.date) {
            dateToUse = normalizeDate(initialState.entry.stock_date || initialState.entry.event_date || initialState.entry.date);
          } else if (initialState.items && initialState.items.length > 0) {
            const firstItem = initialState.items[0];
            const dateValue = firstItem.stock_date || firstItem.event_date || firstItem.date || initialState.date;
            if (dateValue) {
              dateToUse = normalizeDate(dateValue);
            }
          }
        }
        
        // Final fallback to selectedDate (should not happen in edit mode, but safer to have)
        if (!dateToUse) {
          console.error('Stock_out: ⚠️ No original date found, using selectedDate as fallback:', selectedDate);
          dateToUse = selectedDate;
        }
        
        console.log('Stock_out: Using ORIGINAL date for update:', dateToUse);
        console.log('Stock_out: Original date (from entry):', originalDate);
        console.log('Stock_out: Selected date (current form):', selectedDate);
        console.log('Stock_out: Final date to use (must be original):', dateToUse);
        
        // Build update payload with all fields that backend expects
        const updateData = {
          quantity: parseInt(firstRow.quantity, 10),
          reason: firstRow.action === 'Sell' ? 'Sold' : 'Returned',
          stock_date: dateToUse, // Use original date, not selectedDate (which might be today)
          reference_id: referenceId || undefined, // Include reference ID if provided
          selling_price: firstRow.action === 'Sell' ? parseFloat(firstRow.sellingPrice) : undefined,
          notes: firstRow.reason || undefined,
          // Note: vendor_name and expiry_date are not in the form yet, but can be added later
          // vendor_name: undefined,
          // expiry_date: undefined,
        };

        // Remove undefined values to keep payload clean
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === undefined || updateData[key] === null || updateData[key] === '') {
            delete updateData[key];
          }
        });

        console.log('Stock_out: Update payload:', updateData);

        const result = await stockOutApi.updateStockOutEntry(ledgerId, updateData);

        console.log('Stock_out: Update result:', result);
        console.log('Stock_out: Update result.success:', result.success);
        console.log('Stock_out: Update result.data:', result.data);

        if (!result.success) {
          console.error('Stock_out: Update failed:', result.message);
          console.error('Stock_out: Update error details:', result.error);
          console.error('Stock_out: Update error status:', result.status);
          setIsSubmitting(false); // Reset submitting state on error
          
          // Check if it's a path parameter error (backend route configuration issue)
          if (result.message?.includes('Path parameter') || result.error?.includes('Path parameter')) {
            throw new Error('Backend Configuration Error: The API Gateway route for PUT /stock/stock-out/{id} is not properly configured to extract the path parameter. The backend team needs to configure the route to extract the path parameter and pass it to the Lambda function as event.pathParameters.id or event.pathParameters.ledger_id.');
          }
          
          // Check if it's a CORS error
          if (result.message?.includes('CORS') || result.message?.includes('Failed to fetch') || result.status === undefined) {
            throw new Error('CORS Error: The backend API needs to be configured to allow PUT requests. Please contact the backend team to enable CORS for PUT /stock/stock-out/{ledger_id} endpoint.');
          }
          
          throw new Error(result.message || 'Failed to update stock-out');
        }

        console.log('Stock_out: ✅ Update successful, preventing any further submissions');
        setSuccess('Stock-out updated successfully!');
        
        // Disable form to prevent any further submissions
        setIsSubmitting(true); // Keep it true to prevent double submission
        
        // Navigate back to view after a short delay
        setTimeout(() => {
          navigate('/onboarding-stock-out', { replace: true });
          // Force a page refresh to reload data
          window.location.reload();
        }, 1500);
        
        return; // CRITICAL: Return early to prevent falling through to create
      } else {
        console.log('Stock_out: ⚠️ Not in edit mode or ledgerId missing, will CREATE new entry');
        console.log('Stock_out: isEditMode:', isEditMode, 'ledgerId:', ledgerId);
      }

      // CRITICAL: If we're in edit mode but ledgerId is missing, show error instead of creating
      if (isEditMode && !ledgerId) {
        console.error('Stock_out: ❌ Edit mode but ledgerId is missing!');
        console.error('Stock_out: Initial state:', JSON.stringify(initialState, null, 2));
        setIsSubmitting(false);
        throw new Error('Cannot update: Ledger ID is missing. Please go back and try again.');
      }

      // CRITICAL: Prevent creating if we're in edit mode (safety check - should never reach here)
      if (isEditMode) {
        console.error('Stock_out: ❌ CRITICAL: In edit mode but update path was not taken!');
        console.error('Stock_out: This should not happen. ledgerId:', ledgerId);
        setIsSubmitting(false);
        throw new Error('Update failed: Unable to update entry. Please refresh and try again.');
      }

      // Transform form data to API payload for create (only if NOT in edit mode)
      console.log('Stock_out: Creating new stock-out entry (NOT in edit mode)');
      const lines = formRows
        .filter(row => row.stockItemId && row.quantity)
        .map(row => ({
          stock_item_id: row.stockItemId,
          quantity: parseInt(row.quantity, 10),
          reason: row.action === 'Sell' ? 'Sold' : 'Returned',
          selling_price: row.action === 'Sell' ? parseFloat(row.sellingPrice) : undefined,
          notes: row.reason || undefined,
        }));

      const payload = {
        stock_date: selectedDate,
        reference_id: referenceId || undefined,
        lines: lines,
      };

      console.log('Stock_out: Create payload:', payload);
      const result = await stockOutApi.createStockOut(payload);

      console.log('✅ Stock Out Creation: API response:', result);
      console.log('✅ Stock Out Creation: Full response data:', JSON.stringify(result.data, null, 2));
      console.log('✅ Stock Out Creation: Created entries:', result.entries);

      if (!result.success) {
        throw new Error(result.message || 'Failed to create stock-out');
      }

      setSuccess('Stock-out recorded successfully!');
      
      // Log the created entry details for debugging - check all possible field names
      if (result.entries && result.entries.length > 0) {
        result.entries.forEach((entry, idx) => {
          console.log(`📝 Created Entry ${idx + 1} - Full object:`, entry);
          console.log(`📝 Created Entry ${idx + 1} - All keys:`, Object.keys(entry));
          console.log(`📝 Created Entry ${idx + 1} - Extracted fields:`, {
            stock_date: entry.stock_date || entry.event_date || entry.date || entry.stockDate || entry.created_at,
            item_name: entry.item_name || entry.itemName || entry.stock_item_name || entry.name,
            item_id: entry.stock_item_id || entry.item_id || entry.itemId,
            quantity: entry.quantity || entry.qty,
            reason: entry.reason || entry.action,
            site_id: entry.site_id || entry.siteId,
            selling_price: entry.selling_price || entry.sellingPrice || entry.price,
            full_entry: entry
          });
        });
      } else if (result.data) {
        // Check if data has a different structure
        console.log('📝 Response data structure:', Object.keys(result.data));
        if (result.data.entries) {
          console.log('📝 Found entries in data.entries:', result.data.entries);
        }
        if (result.data.stock_out_entries) {
          console.log('📝 Found entries in data.stock_out_entries:', result.data.stock_out_entries);
        }
      }
      
      // Trigger refresh in view page (if open in another tab)
      localStorage.setItem('stockOutCreated', 'true');
      
      // Reset form
      setFormRows([createEmptyRow()]);
      setReferenceId('');
      setIsSubmitting(false);
      
      // Navigate back to view page and refresh after a short delay
      setTimeout(() => {
        navigate('/onboarding-stock-out', { replace: true });
        // Force a page refresh to reload data
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('Error in stock-out submission:', error);
      
      // In edit mode, if there's an error, don't allow falling through to create
      if (isEditMode) {
        console.error('Stock_out: ❌ Error in edit mode - preventing any further action');
        setError(error.message || 'Failed to update stock-out entry. Please try again.');
        setIsSubmitting(false);
        return; // CRITICAL: Return early to prevent any further execution
      }
      
      // For create mode, show the error
      setError(error.message || 'Failed to record stock-out. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    // Extract ledger ID (same logic as in handleSubmit)
    let ledgerId = initialState.entryId;
    
    if (!ledgerId && initialState.entry) {
      ledgerId = initialState.entry.ledger_id || 
                 initialState.entry.stock_out_record_id || 
                 initialState.entry.stock_out_id || 
                 initialState.entry.id ||
                 initialState.entry.ledger_entry_id;
    }
    
    if (!ledgerId && initialState.items && initialState.items.length > 0) {
      const firstItem = initialState.items[0];
      ledgerId = firstItem.ledger_id || 
                 firstItem.stock_out_record_id || 
                 firstItem.stock_out_id || 
                 firstItem.id;
    }
    
    if (!ledgerId) {
      setError('Cannot delete: Ledger ID is missing. Please refresh the page and try again.');
      setShowDeleteConfirm(false);
      return;
    }
    
    setIsDeleting(true);
    setError('');
    
    try {
      console.log('Stock_out: Deleting entry with ledger_id:', ledgerId);
      
      const result = await stockOutApi.deleteStockOutEntry(ledgerId);
      
      if (result.success) {
        setSuccess('Stock-out entry deleted successfully. Stock has been restored to inventory.');
        setShowDeleteConfirm(false);
        
        // Navigate back to stock-out view after a short delay
        setTimeout(() => {
          navigate('/onboarding-stock-out', { replace: true });
          // Force a page refresh to reload data
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(result.message || 'Failed to delete stock-out entry');
      }
    } catch (err) {
      console.error('Stock_out: Error deleting entry:', err);
      setError(err.message || 'Failed to delete stock-out entry. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const totalRevenue = useMemo(() => {
    return formRows
      .filter(row => row.action === 'Sell' && row.sellingPrice && row.quantity)
      .reduce((sum, row) => sum + (parseFloat(row.sellingPrice) * parseFloat(row.quantity)), 0);
  }, [formRows]);

  return (
    <div className="w-full h-full min-h-screen">
      <div className="mx-auto h-auto max-w-7xl">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header with Title */}
          <div className="mb-8">
            <h1 className={typography.h2} style={{ color: '#000000' }}>
              {isEditMode ? 'Stock out details' : 'Create Stock Out'}
            </h1>
            <p className="mt-2 text-gray-600">
              {isEditMode 
                ? 'View and edit stock-out entry details' 
                : 'Record new stock-out entries'}
            </p>
          </div>

          <div className="rounded-3xl">
            {/* Stock Out Button - Appears when items are pre-filled from date selection */}
            {/* {hasItemsFromState && (
              <div className="mb-6 flex justify-end">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                >
                  Stock Out
                </button>
              </div>
            )} */}
            
            <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
                {/* Site Selection */}
                <div className="min-w-[200px]">
                  <label htmlFor="stock-out-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <MapPin className="w-4 h-4" />
                    Select Site <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="stock-out-site"
                    value={selectedSite ? (selectedSite.site_id || selectedSite.id) : ''}
                    onChange={(e) => {
                      const siteId = e.target.value;
                      const site = sites.find(s => (s.site_id || s.id) === siteId);
                      handleSiteChange(siteId);
                    }}
                    disabled={isLoadingSites || isEditMode}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  >
                    <option value="">
                      {isLoadingSites ? 'Loading sites...' : 'Select a site'}
                    </option>
                    {/* alphabetical order */}
                    {Array.isArray(sites) && sites.map((site) => (
                      <option key={site.site_id || site.id} value={site.site_id || site.id}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                  </select>
                </div>

              <div className="flex-1">
                <label htmlFor="stock-out-date" className="inline-flex items-center gap-2">
                  Pick Date
                </label>
                <div className="relative mt-1 max-w-xs">
                  <Input
                    id="stock-out-date"
                    type="date"
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                      disabled={hasItemsFromState || isEditMode}
                      readOnly={hasItemsFromState || isEditMode}
                      className={`w-full h-12 rounded-2xl border pr-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500 ${
                        hasItemsFromState || isEditMode ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                      }`}
                  />
                </div>
              </div>

                {/* Reference ID */}
                <div>
                  <label htmlFor="stock-out-reference" className="mb-2 block text-sm font-semibold text-gray-700">
                    Reference ID (Optional)
                  </label>
                  <Input
                    id="stock-out-reference"
                    placeholder="e.g., SO-2025-0001"
                    value={referenceId}
                    onChange={(event) => setReferenceId(event.target.value)}
                    className="w-full h-12 rounded-2xl border bg-white pr-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500"
                  />
                </div>
            </div>

              {/* Stock Out Items Table */}
              <div className="relative group">
                {!isMobileView && (
                  <>
                    <div className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                        <th className="px-4 py-3 font-semibold min-w-[200px]">
                          Stock Item <span className="text-red-500">*</span>
                      </th>
                      <th className="px-4 py-3 font-semibold">
                          Available
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">
                          Quantity <span className="text-red-500">*</span>
                        </th>
                        <th className="px-4 py-3 font-semibold min-w-[160px]">
                          Action <span className="text-red-500">*</span>
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">
                          Selling Price
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[220px]">
                          Reason/Notes
                      </th>
                        <th className="px-4 py-3 text-right font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                      {formRows.map((row, index) => (
                        <tr
                          key={row.id}
                          className="border-t border-gray-100 text-gray-800"
                        >
                          <td className="px-4 py-4 text-center">{index + 1}</td>
                          <td className="px-4 py-4">
                            <select
                              value={row.stockItemId}
                              onChange={(event) =>
                                handleRowChange(row.id, 'stockItemId', event.target.value)
                              }
                              disabled={!selectedSite || isLoadingStockItems}
                              className="w-full h-11 rounded-2xl border bg-white px-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              required
                            >
                              <option value="">
                                {isLoadingStockItems ? 'Loading...' : 'Select item'}
                              </option>
                              {stockItems.map((item) => (
                                <option
                                  key={item.stock_item_id || item.item_id}
                                  value={item.stock_item_id || item.item_id}
                                >
                                  {item.item_name || item.name} 
                                  {item.unit_of_measure ? ` (${item.unit_of_measure})` : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-4">
                            {row.stockItemId ? (
                              <span className="text-sm">
                                {row.availableQuantity} {stockItems.find(i => (i.stock_item_id || i.item_id) === row.stockItemId)?.unit_of_measure || ''}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={row.quantity}
                              onChange={(event) => {
                                const value = event.target.value;
                                // Only allow whole numbers (no decimals)
                                if (value === '' || /^\d+$/.test(value)) {
                                  handleRowChange(row.id, 'quantity', value);
                                }
                              }}
                              onInput={(event) => {
                                // Remove any decimal point or decimal digits
                                const value = event.target.value;
                                if (value.includes('.')) {
                                  event.target.value = value.split('.')[0];
                                  handleRowChange(row.id, 'quantity', value.split('.')[0]);
                                }
                              }}
                              className="h-11 rounded-2xl border bg-white px-4 text-sm shadow-none border-gray-200 focus:ring-green-500"
                              required
                            />
                            {row.stockItemId && row.quantity && parseFloat(row.quantity) > row.availableQuantity && (
                              <p className="mt-1 text-xs text-red-500">
                                Exceeds available
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-4">
                              <select
                              value={row.action}
                                onChange={(event) =>
                                handleRowChange(row.id, 'action', event.target.value)
                                }
                                className="h-11 rounded-2xl border bg-white px-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500"
                              required
                              >
                                <option value="Return">Return</option>
                                <option value="Sell">Sell</option>
                              </select>
                          </td>
                          <td className="px-4 py-4">
                            {row.action === 'Sell' ? (
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                placeholder="0.00"
                                value={row.sellingPrice}
                                  onChange={(event) =>
                                  handleRowChange(row.id, 'sellingPrice', event.target.value)
                                  }
                                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none border-gray-200 focus:ring-green-500"
                                required={row.action === 'Sell'}
                                />
                            ) : (
                              <span className="text-gray-400 text-sm">N/A</span>
                              )}
                          </td>
                          <td className="px-4 py-4">
                            <textarea
                              rows={2}
                              placeholder="Add reason/notes"
                              value={row.reason}
                              onChange={(event) =>
                                handleRowChange(row.id, 'reason', event.target.value)
                              }
                              className="w-full rounded-2xl border bg-gray-50 px-4 py-2 text-sm text-gray-700 shadow-none border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                            />
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(row.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-200 bg-gray-50">
                        <td colSpan={8} className="px-4 py-3">
                          <button
                            type="button"
                            onClick={handleAddRow}
                            className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-500"
                          >
                            + Add Item
                          </button>
                        </td>
                      </tr>
                  </tbody>
                    {formRows.some(row => row.action === 'Sell' && row.sellingPrice && row.quantity) && (
                  <tfoot>
                    <tr className="border-t border-gray-200 bg-gray-50 text-gray-700 font-semibold">
                          <td className="px-4 py-3" colSpan={6}>
                            Total Revenue (Sold Items)
                      </td>
                          <td className="px-4 py-3" colSpan={2}>
                        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-2 text-right">
                              ₹{totalRevenue.toFixed(2)}
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                    )}
                        </table>
                      </div>
                    </div>
                  </>
                )}

                {isMobileView && (
                  <div 
                    className="space-y-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm"
                    style={{ 
                      overscrollBehaviorX: 'none',
                      overflowX: 'hidden',
                      direction: 'ltr'
                    }}
                  >
                    {formRows.map((row, index) => (
                      <div
                        key={row.id}
                        className="rounded-2xl border border-gray-100 p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-700">
                            Item #{index + 1}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(row.id)}
                            disabled={formRows.length === 1}
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              formRows.length === 1
                                ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                            }`}
                          >
                            <XCircle className="h-4 w-4" />
                            Remove
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Stock Item <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={row.stockItemId}
                              onChange={(event) =>
                                handleRowChange(row.id, 'stockItemId', event.target.value)
                              }
                              disabled={!selectedSite || isLoadingStockItems}
                              className="w-full h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              required
                            >
                              <option value="">
                                {isLoadingStockItems ? 'Loading...' : 'Select item'}
                              </option>
                              {stockItems.map((item) => (
                                <option
                                  key={item.stock_item_id || item.item_id}
                                  value={item.stock_item_id || item.item_id}
                                >
                                  {item.item_name || item.name} 
                                  {item.unit_of_measure ? ` (${item.unit_of_measure})` : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Available
                              </label>
                              {row.stockItemId ? (
                                <div className="h-11 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-sm text-gray-700">
                                  {row.availableQuantity} {stockItems.find(i => (i.stock_item_id || i.item_id) === row.stockItemId)?.unit_of_measure || ''}
                                </div>
                              ) : (
                                <div className="h-11 w-full rounded-2xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-sm text-gray-400">
                                  -
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Quantity <span className="text-red-500">*</span>
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="0"
                                value={row.quantity}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  // Only allow whole numbers (no decimals)
                                  if (value === '' || /^\d+$/.test(value)) {
                                    handleRowChange(row.id, 'quantity', value);
                                  }
                                }}
                                onInput={(event) => {
                                  // Remove any decimal point or decimal digits
                                  const value = event.target.value;
                                  if (value.includes('.')) {
                                    event.target.value = value.split('.')[0];
                                    handleRowChange(row.id, 'quantity', value.split('.')[0]);
                                  }
                                }}
                                className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none border-gray-200 focus:ring-green-500"
                                required
                              />
                              {row.stockItemId && row.quantity && parseFloat(row.quantity) > row.availableQuantity && (
                                <p className="mt-1 text-xs text-red-500">
                                  Exceeds available
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Action <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={row.action}
                                onChange={(event) =>
                                  handleRowChange(row.id, 'action', event.target.value)
                                }
                                className="h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500"
                                required
                              >
                                <option value="Return">Return</option>
                                <option value="Sell">Sell</option>
                              </select>
                            </div>
                            {row.action === 'Sell' && (
                              <div>
                                <label className="mb-1 block text-xs font-semibold text-gray-500">
                                  Selling Price <span className="text-red-500">*</span>
                                </label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={row.sellingPrice}
                                  onChange={(event) =>
                                    handleRowChange(row.id, 'sellingPrice', event.target.value)
                                  }
                                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none border-gray-200 focus:ring-green-500"
                                  required
                                />
                              </div>
                            )}
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-500">
                                Reason/Notes
                              </label>
                              <textarea
                                rows={2}
                                placeholder="Add reason/notes"
                                value={row.reason}
                                onChange={(event) =>
                                  handleRowChange(row.id, 'reason', event.target.value)
                                }
                                className="w-full rounded-2xl border bg-gray-50 px-4 py-2 text-sm text-gray-700 shadow-none border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleAddRow}
                        className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add Item
                      </button>
                    </div>

                    {formRows.some(row => row.action === 'Sell' && row.sellingPrice && row.quantity) && (
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-right text-sm font-semibold text-gray-700">
                        Total Revenue (Sold Items): ₹{totalRevenue.toFixed(2)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Error and Success Messages */}
              {error && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              )}
              {success && (
                <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3">
                  <p className="text-sm font-medium text-green-800">{success}</p>
                </div>
              )}

              {/* Submit and Delete Buttons */}
              <div className={`flex ${isMobileView ? 'flex-col gap-3' : 'justify-between items-center'} mt-4`}>
                {/* Delete Button - Only show in edit mode */}
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isDeleting || isSubmitting}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 text-white shadow-sm transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isMobileView 
                        ? 'w-full h-14 px-6 py-3 text-xl font-bold font-[\'Figtree\']' 
                        : 'px-4 py-2 text-sm font-semibold rounded-xl'
                    }`}
                    style={{ color: '#ffffff' }}
                  >
                    <span
                      style={{
                        color: '#ffffff !important',
                        WebkitTextFillColor: '#ffffff',
                        WebkitTextStroke: 'none',
                      }}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Entry'}
                    </span>
                  </button>
                )}
                
                {/* Submit Button */}
                <div className={isMobileView ? 'w-full' : 'flex justify-end'}>
                  <PrimaryButton 
                    type="submit"
                    disabled={isSubmitting || !selectedSite}
                    className={isMobileView ? 'w-full' : ''}
                  >
                    {isSubmitting ? 'Submitting...' : isEditMode ? 'Update Stock Out' : 'Record Stock Out'}
                  </PrimaryButton>
                </div>
              </div>
              
              {/* Delete Confirmation Dialog */}
              {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Delete Stock-Out Entry?
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Are you sure you want to delete this stock-out entry? This action will:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Reverse the stock deduction (add quantity back to inventory)</li>
                        <li>Permanently delete this entry</li>
                      </ul>
                      This action cannot be undone.
                    </p>
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Stock_out;

